import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Alert,
  Button,
  CircularProgress,
  Grid,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Switch,
  FormControlLabel,
  ToggleButtonGroup,
  ToggleButton,
  Tab,
  Tabs
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteIcon from "@mui/icons-material/Delete";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FolderIcon from "@mui/icons-material/Folder";
import SortIcon from "@mui/icons-material/Sort";
import ComputerIcon from '@mui/icons-material/Computer';
import StorageIcon from '@mui/icons-material/Storage';
import ScheduleIcon from '@mui/icons-material/Schedule';
import api from "../api";
import logger from './../utils/logger.js';

const LogViewerTab = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [logData, setLogData] = useState([]);
  const [logInfo, setLogInfo] = useState({ size: 0, rotation_enabled: false, num_files: 0 });
  const [logFiles, setLogFiles] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    level: "",
    module: "",
    search: "",
    from_date: "",
    to_date: ""
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showLogFilesDialog, setShowLogFilesDialog] = useState(false);
  const [selectedLogFile, setSelectedLogFile] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [autoReload, setAutoReload] = useState(false);
  const [autoReloadInterval, setAutoReloadInterval] = useState(10); // seconds
  const autoReloadTimer = useRef(null);
  const [logSource, setLogSource] = useState("frontend"); // "frontend" or "backend" or "dns-scheduler"
  const [availableModules, setAvailableModules] = useState([]);

  // Fetch log data on component mount and when filters change
  useEffect(() => {
    fetchLogs();
    return () => {
      // Clean up auto-reload timer when component unmounts
      if (autoReloadTimer.current) {
        clearInterval(autoReloadTimer.current);
      }
    };
  }, [page, logSource]);

  // Handle auto-reload toggling
  useEffect(() => {
    if (autoReloadTimer.current) {
      clearInterval(autoReloadTimer.current);
      autoReloadTimer.current = null;
    }

    if (autoReload) {
      autoReloadTimer.current = setInterval(() => {
        fetchLogs(false); // Don't show loading indicator for auto-reload
      }, autoReloadInterval * 1000);
    }

    return () => {
      if (autoReloadTimer.current) {
        clearInterval(autoReloadTimer.current);
      }
    };
  }, [autoReload, autoReloadInterval, filters, page, logSource]);

  // Handler for changing log source
  const handleLogSourceChange = (event, newSource) => {
    if (newSource !== null) {
      setLogSource(newSource);
      setPage(1); // Reset to first page when changing source
      setError(null);
    }
  };

  // Fetch log files separately
  const fetchLogFiles = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get("/api/admin/logs", {
        params: {
          show_files: true,
          source: logSource
        }
      });

      if (response.data.success) {
        setLogFiles(response.data.log_files || []);
        setLogInfo({
          size: response.data.log_size || 0,
          rotation_enabled: response.data.rotation_enabled || false,
          num_files: response.data.num_log_files || 0
        });
        setAvailableModules(response.data.available_modules || []);
      } else {
        setError(response.data.message || "Failed to load log files");
      }
    } catch (error) {
      setError(error.response?.data?.message || "An error occurred while fetching log files");
      logger.error("Error fetching log files:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async (showLoadingIndicator = true) => {
    try {
      if (showLoadingIndicator) {
        setLoading(true);
      }
      setError(null);

      const response = await api.get("/api/admin/logs", {
        params: {
          page,
          per_page: 50,
          level: filters.level,
          module: filters.module,
          search: filters.search,
          from_date: filters.from_date,
          to_date: filters.to_date,
          source: logSource
        }
      });

      if (response.data.success) {
        // Reverse the logs to show newest first
        const logs = response.data.logs || [];
        setLogData(logs.reverse());
        setTotalPages(response.data.total_pages || 1);
        setLogInfo({
          size: response.data.log_size || 0,
          rotation_enabled: response.data.rotation_enabled || false,
          num_files: response.data.num_log_files || 0
        });
        setLogFiles(response.data.log_files || []);
        setAvailableModules(response.data.available_modules || []);
      } else {
        setError(response.data.message || "Failed to load logs");
      }
    } catch (error) {
      setError(error.response?.data?.message || "An error occurred while fetching logs");
      logger.error("Error fetching logs:", error);
    } finally {
      if (showLoadingIndicator) {
        setLoading(false);
      }
    }
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAutoReloadIntervalChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= 5) {
      setAutoReloadInterval(value);
    }
  };

  const toggleAutoReload = () => {
    setAutoReload(!autoReload);
  };

  const applyFilters = () => {
    setPage(1); // Reset to first page when applying new filters
    fetchLogs();
  };

  const resetFilters = () => {
    setFilters({
      level: "",
      module: "",
      search: "",
      from_date: "",
      to_date: ""
    });
    setPage(1);
    fetchLogs();
  };

  const downloadLogs = async () => {
    try {
      const response = await api.get("/api/admin/logs/download", {
        responseType: "blob",
        params: {
          level: filters.level,
          module: filters.module,
          search: filters.search,
          from_date: filters.from_date,
          to_date: filters.to_date,
          source: logSource
        }
      });

      // Create a download link and trigger it
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${logSource.replace('-', '_')}_log_${new Date().toISOString().slice(0, 10)}.log`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      setError("Failed to download logs");
      logger.error("Error downloading logs:", error);
    }
  };

  const downloadLogFile = async (fileName) => {
    try {
      const response = await api.get("/api/admin/logs/download", {
        responseType: "blob",
        params: {
          file: fileName,
          source: logSource
        }
      });

      // Create a download link and trigger it
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      setError(`Failed to download log file: ${fileName}`);
      logger.error("Error downloading log file:", error);
    }
  };

  const clearLogs = async () => {
    if (!window.confirm("Are you sure you want to clear all logs? This action cannot be undone.")) {
      return;
    }

    try {
      setLoading(true);
      const response = await api.post("/api/admin/logs/clear", {
        source: logSource
      });
      if (response.data.success) {
        setLogData([]);
        setLogInfo({ ...logInfo, size: 0 });
        setError(null);
        fetchLogs(); // Refresh log data
      } else {
        setError(response.data.message || "Failed to clear logs");
      }
    } catch (error) {
      setError(error.response?.data?.message || "An error occurred while clearing logs");
      logger.error("Error clearing logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const deleteLogFile = async (fileName) => {
    try {
      setLoading(true);
      const response = await api.post("/api/admin/logs/clear", {
        file: fileName,
        source: logSource
      });
      
      if (response.data.success) {
        setError(null);
        setDeleteConfirmOpen(false);
        setSelectedLogFile(null);
        await fetchLogFiles(); // Refresh log files list
        await fetchLogs(); // Refresh log data
      } else {
        setError(response.data.message || `Failed to delete log file: ${fileName}`);
      }
    } catch (error) {
      setError(error.response?.data?.message || `An error occurred while deleting log file: ${fileName}`);
      logger.error("Error deleting log file:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLogFileClick = (file) => {
    setSelectedLogFile(file);
    setDeleteConfirmOpen(true);
  };

  const toggleLogRotation = async () => {
    try {
      setLoading(true);
      const response = await api.post("/api/admin/logs/configure", {
        enable_rotation: !logInfo.rotation_enabled,
        source: logSource
      });
      
      if (response.data.success) {
        setLogInfo({
          ...logInfo,
          rotation_enabled: response.data.rotation_enabled
        });
        setError(null);
      } else {
        setError(response.data.message || "Failed to update log rotation configuration");
      }
    } catch (error) {
      setError(error.response?.data?.message || "An error occurred while updating log configuration");
      logger.error("Error updating log configuration:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return "0 Bytes";
    
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  const getLevelColor = (level) => {
    switch (level.toUpperCase()) {
      case "ERROR":
        return "error";
      case "WARNING":
        return "warning";
      case "INFO":
        return "info";
      case "DEBUG":
        return "success";
      default:
        return "default";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const handleOpenLogFilesDialog = async () => {
    await fetchLogFiles();
    setShowLogFilesDialog(true);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography variant="h6" sx={{ mr: 3 }}>
            Application Logs
            {autoReload && <Chip 
              label={`Auto-refresh: ${autoReloadInterval}s`} 
              color="primary" 
              size="small" 
              sx={{ ml: 2 }} 
            />}
          </Typography>
          
          {/* Log Source Toggle */}
          <ToggleButtonGroup
            value={logSource}
            exclusive
            onChange={handleLogSourceChange}
            aria-label="log source"
            size="small"
          >
            <ToggleButton value="frontend" aria-label="frontend logs">
              <ComputerIcon sx={{ mr: 1 }} />
              Frontend
            </ToggleButton>
            <ToggleButton value="backend" aria-label="backend logs">
              <StorageIcon sx={{ mr: 1 }} />
              Backend
            </ToggleButton>
            <ToggleButton value="dns-scheduler" aria-label="dns-scheduler logs">
              <ScheduleIcon sx={{ mr: 1 }} />
              DNS Scheduler
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<FilterAltIcon />}
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? "Hide Filters" : "Show Filters"}
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<FolderIcon />}
            onClick={handleOpenLogFilesDialog}
          >
            Log Files ({logInfo.num_files})
          </Button>
          
          <Tooltip title="Refresh logs">
            <IconButton color="primary" onClick={() => fetchLogs()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Download logs">
            <IconButton color="primary" onClick={downloadLogs}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Clear logs">
            <IconButton color="error" onClick={clearLogs}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body1">
              <strong>Log Size:</strong> {formatBytes(logInfo.size)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body1">
              <strong>Log Rotation:</strong> {logInfo.rotation_enabled ? "Enabled" : "Disabled"}
              {logInfo.num_files > 0 && ` (${logInfo.num_files} files)`}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: { xs: "flex-start", md: "flex-end" }}}>
              <Button
                variant={logInfo.rotation_enabled ? "outlined" : "contained"}
                color={logInfo.rotation_enabled ? "secondary" : "primary"}
                onClick={toggleLogRotation}
                disabled={loading}
                size="small"
              >
                {logInfo.rotation_enabled ? "Disable Rotation" : "Enable Rotation"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Auto-Refresh:</strong> Automatically refresh logs at specified interval
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={autoReload}
                    onChange={toggleAutoReload}
                    color="primary"
                  />
                }
                label={autoReload ? "Enabled" : "Disabled"}
              />
              {autoReload && (
                <TextField
                  variant="outlined"
                  label="Interval (seconds)"
                  type="number"
                  size="small"
                  value={autoReloadInterval}
                  onChange={handleAutoReloadIntervalChange}
                  sx={{ ml: 2, width: 120 }}
                  inputProps={{ min: 5 }}
                />
              )}
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" gutterBottom>
              <strong>Log Order:</strong> Newest entries are shown first
            </Typography>
            <Tooltip title="Logs are sorted in reverse chronological order (newest first)">
              <Chip 
                label="Newest First"
                color="primary"
                size="small"
                icon={<SortIcon />}
              />
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {showFilters && (
        <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Filter Logs
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel>Log Level</InputLabel>
                <Select
                  name="level"
                  value={filters.level}
                  onChange={handleFilterChange}
                  label="Log Level"
                >
                  <MenuItem value="">All Levels</MenuItem>
                  <MenuItem value="DEBUG">Debug</MenuItem>
                  <MenuItem value="INFO">Info</MenuItem>
                  <MenuItem value="WARNING">Warning</MenuItem>
                  <MenuItem value="ERROR">Error</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel>Module</InputLabel>
                <Select
                  name="module"
                  value={filters.module}
                  onChange={handleFilterChange}
                  label="Module"
                >
                  <MenuItem value="">All Modules</MenuItem>
                  {availableModules.map((module) => (
                    <MenuItem key={module} value={module}>{module}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                fullWidth
                size="small"
                label="From Date"
                name="from_date"
                type="date"
                value={filters.from_date}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={2.4}>
              <TextField
                fullWidth
                size="small"
                label="To Date"
                name="to_date"
                type="date"
                value={filters.to_date}
                onChange={handleFilterChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button variant="outlined" onClick={resetFilters}>
              Reset
            </Button>
            <Button variant="contained" onClick={applyFilters}>
              Apply Filters
            </Button>
          </Box>
        </Paper>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      ) : logData.length === 0 ? (
        <Alert severity="info">No log entries found</Alert>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell width="15%">Timestamp</TableCell>
                  <TableCell width="10%">Level</TableCell>
                  <TableCell width="15%">Module</TableCell>
                  <TableCell>Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logData.filter(log => log.message && log.message.trim() !== '').map((log, index) => (
                  <TableRow key={index} hover>
                    <TableCell>{log.timestamp}</TableCell>
                    <TableCell>
                      <Chip
                        label={log.level}
                        size="small"
                        color={getLevelColor(log.level)}
                      />
                    </TableCell>
                    <TableCell>{log.module}</TableCell>
                    <TableCell sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{log.message}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        </>
      )}

      {/* Log Files Dialog */}
      <Dialog 
        open={showLogFilesDialog} 
        onClose={() => setShowLogFilesDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {logSource === "frontend" ? "Frontend Log Files" : logSource === "backend" ? "Backend Log Files" : "DNS Scheduler Log Files"}
        </DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {logSource === "frontend" && "These files contain frontend application logs from the React application."}
            {logSource === "backend" && "These files contain backend application logs from the Flask server."}
            {logSource === "dns-scheduler" && "These files contain DNS scheduler logs showing domain checking operations and status updates."}
            {" "}The main log file (app.log) is actively being written to, while rotated files are archived copies.
          </Typography>
          
          {logFiles.length === 0 ? (
            <Alert severity="info">No log files found</Alert>
          ) : (
            <List>
              {logFiles.map((file, index) => (
                <React.Fragment key={file.name}>
                  {index > 0 && <Divider />}
                  <ListItem>
                    <ListItemText
                      primary={file.name}
                      secondary={
                        <>
                          <Typography component="span" variant="body2" color="text.primary">
                            {formatBytes(file.size)}
                          </Typography>
                          {" — "}
                          {formatDate(file.modified)}
                          {file.is_rotated && " (Rotated)"}
                        </>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Download file">
                        <IconButton edge="end" aria-label="download" onClick={() => downloadLogFile(file.name)}>
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={file.is_rotated ? "Delete file" : "Clear file"}>
                        <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteLogFileClick(file)} sx={{ ml: 1 }}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLogFilesDialog(false)}>Close</Button>
          <Button onClick={fetchLogFiles} startIcon={<RefreshIcon />}>Refresh</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>
          <Typography>
            {selectedLogFile?.is_rotated ? 
              `Are you sure you want to delete the log file "${selectedLogFile?.name}"?` :
              `Are you sure you want to clear the main log file "${selectedLogFile?.name}"?`
            }
            {selectedLogFile?.is_rotated && " This action cannot be undone."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button 
            onClick={() => selectedLogFile && deleteLogFile(selectedLogFile.name)} 
            color="error"
            variant="contained"
          >
            {selectedLogFile?.is_rotated ? "Delete" : "Clear"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LogViewerTab; 